import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { CheckCircle, ArrowRight, LayoutDashboard, Home } from "lucide-react";

const MerchantPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { token, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/merchant/subscription/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === "paid") {
          setStatus("success");
          // Refresh user data to get updated approval status
          await checkAuth();
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl border border-slate-800 p-8 text-center shadow-lg">
          {status === "checking" ? (
            <>
              <div className="w-16 h-16 mx-auto mb-6">
                <div className="w-full h-full border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                Processing Payment
              </h1>
              <p className="text-gray-500 mb-6">
                Please wait while we confirm your subscription...
              </p>
            </>
          ) : status === "success" ? (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2" data-testid="merchant-payment-success">
                Welcome to <span className="font-brand">VEYRONIX</span>!
              </h1>
              <p className="text-gray-500 mb-8">
                Your merchant account is now active. You can start listing products and selling immediately.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate("/merchant")}
                  className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 rounded-full"
                  data-testid="go-to-dashboard-btn"
                >
                  <LayoutDashboard className="h-5 w-5 mr-2" />
                  Go to Dashboard
                </Button>
                <Link to="/">
                  <Button variant="outline" className="w-full h-12 border-slate-700 text-white hover:bg-gray-100 rounded-full">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <LayoutDashboard className="h-10 w-10 text-amber-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                Payment Status Unknown
              </h1>
              <p className="text-gray-500 mb-8">
                We couldn't confirm your payment. Please check your email or contact support.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/">
                  <Button className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 rounded-full">
                    <Home className="h-5 w-5 mr-2" />
                    Return Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MerchantPaymentSuccess;
